import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAdminToken } from '../../admin/sessionSlice';
import { fetchThemeData } from '../../admin/themeSlice';
import { initStageReport, fetchGameStages } from '../../functions/stageReport';
import { isSessionEndedError, randomDelayMs } from '../../utils/canvasCompletion';
import { mergeGameAssignmentIntoUserData, readStoredUserData } from '../../utils/gameStages';
import {
  buildFirebaseSessionId,
  sanitizeFirebaseKey,
  subscribeSessionUsers,
  registerUserCanvasSession,
  subscribeWaitingRoom,
  armWaitingRoomDisconnectCleanup,
} from '../../utils/canvasFirebase';
import { formatGameTimer } from '../../utils/gameTimer';
import './waiting-room.css';

const DEFAULT_WAITING_SECONDS = 5 * 60;
// Players enter the canvas over a short window instead of all in the same instant.
const LOBBY_HANDOFF_SPREAD_MS = 3000;
const MAX_VISIBLE_AVATARS = 6;
const AVATAR_COLORS = [
  '#f472b6',
  '#fb923c',
  '#a78bfa',
  '#fbbf24',
  '#60a5fa',
  '#34d399',
  '#f87171',
  '#818cf8',
];
const RING_RADIUS = 70;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Deterministic pseudo-random color per player name (falls back to index cycling). */
function avatarColorForName(name, index) {
  const str = String(name ?? '').trim();
  if (!str) return AVATAR_COLORS[index % AVATAR_COLORS.length];
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function initialForName(name) {
  const trimmed = String(name ?? '').trim();
  return trimmed ? trimmed[0].toUpperCase() : '?';
}

function waitingRoomThemeVisuals(themeData) {
  const t = themeData || {};
  const textColor = (t.text_color != null && String(t.text_color).trim()) || '#1e2a4a';
  const buttonBgColor =
    (t.button_background_color != null && String(t.button_background_color).trim()) ||
    (t.box_bg_color != null && String(t.box_bg_color).trim()) ||
    '#6845d3';
  // Box text color is the one setting for all box/button-styled text on this
  // page — a separate "Button text color" field used to exist too, which just
  // meant setting Box text color alone looked like it did nothing whenever a
  // Button text color had also been set at some point.
  const boxTextColor = (t.box_text_color != null && String(t.box_text_color).trim()) || '#ffffff';
  return { textColor, buttonBgColor, boxTextColor };
}

export default function WaitingRoom() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const authUser = useSelector((state) => state.auth?.user);
  const { status: themeStatus, data: themeData, currentTheme } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [usersMap, setUsersMap] = useState({});
  const [remainingSeconds, setRemainingSeconds] = useState(DEFAULT_WAITING_SECONDS);
  const [ringDuration, setRingDuration] = useState(DEFAULT_WAITING_SECONDS);
  const [estimatedStartLabel, setEstimatedStartLabel] = useState('');
  const [timerStarted, setTimerStarted] = useState(false);

  const initCalledRef = useRef(false);
  const navigatedRef = useRef(false);
  const tickIntervalRef = useRef(null);
  const waitingRoomStateRef = useRef({ startedAt: null, durationSeconds: DEFAULT_WAITING_SECONDS });
  const unsubscribersRef = useRef([]);
  const usersMapRef = useRef({});

  // Ensure theme data is loaded (mirrors the DDDUI `USER` wrapper's own load, defensively —
  // this page can also be reached directly rather than only via the wrapper's effect timing).
  useEffect(() => {
    if (themeStatus === 'idle') {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  const visuals = useMemo(() => waitingRoomThemeVisuals(themeData), [themeData]);

  const computeRemaining = useCallback(() => {
    const { startedAt, durationSeconds } = waitingRoomStateRef.current;
    if (!startedAt) return durationSeconds;
    const elapsed = (Date.now() - startedAt) / 1000;
    return Math.max(0, durationSeconds - elapsed);
  }, []);

  useEffect(() => {
    if (initCalledRef.current) return;
    if (themeStatus !== 'succeeded' || !themeData) return;
    if (!authUser) return;

    // This page is Live-mode only — no matter how a player got here (bookmark,
    // back button, stale link), Self-Paced skips the lobby entirely.
    if (themeData.game_mode === 'self_paced') {
      initCalledRef.current = true;
      navigate('/canvas', { replace: true });
      return;
    }

    initCalledRef.current = true;

    const run = async () => {
      const userId = authUser?.userId;
      const organizationId = authUser?.organizationId;
      const sessionId = authUser?.sessionId;
      const token =
        adminToken || (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

      if (!token || !userId || !organizationId || !sessionId) {
        setMessage('Unable to load waiting room. Please sign in again.');
        setLoading(false);
        return;
      }

      try {
        const stored = readStoredUserData();
        let puzzleId = stored?.puzzle_id ?? stored?.puzzleid ?? null;

        const initData = await initStageReport({
          token,
          userId,
          email: authUser?.email,
          name: authUser?.name,
          userType: authUser?.role || authUser?.source || 'ORG_USER',
          reportId: authUser?.reportId || '',
          themeName: 'Collaborative Canvas',
          totalPuzzles: themeData?.total_puzzles ?? themeData?.puzzle_parts ?? 9,
          currentTheme,
        });
        puzzleId = puzzleId ?? initData?.game?.stages?.puzzle_id ?? initData?.game?.puzzle_id ?? null;

        const fbSessionId = buildFirebaseSessionId(organizationId, sessionId);
        const fbUserId = sanitizeFirebaseKey(userId);

        // Register Firebase presence as soon as a seat is assigned — before the
        // fetchGameStages round-trip below, which can fail independently (network
        // blip, expired session, etc). initStageReport already permanently reserves
        // a puzzle_id slot in MySQL for this user; if a later step in this effect
        // throws, the seat stays taken but the player would never appear in the
        // lobby's Firebase-driven "Players Joined" count, letting the pool silently
        // fill past what the lobby shows (e.g. "5/6 joined" while the 6th slot is
        // actually already spoken for by a player who never made it this far).
        await registerUserCanvasSession(fbSessionId, fbUserId, {
          userId: String(userId),
          name: authUser?.name || authUser?.email || 'Player',
          puzzle_id: puzzleId != null ? Number(puzzleId) : null,
          status: 'waiting',
        });
        // A player who closes this tab before the lobby ends used to remain
        // counted as "present" forever (the Firebase node just stays), so the
        // lobby-timeout auto-fill skipped their tile as if they were still
        // here to finish it themselves. This arms Firebase's own
        // onDisconnect so the node is actually removed the moment the
        // connection really drops; cancelWaitingRoomDisconnect (below) disarms
        // it again once they move on into real gameplay.
        const cancelWaitingRoomDisconnect = armWaitingRoomDisconnectCleanup(fbSessionId, fbUserId);
        unsubscribersRef.current.push(cancelWaitingRoomDisconnect);

        const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
        mergeGameAssignmentIntoUserData({
          game: stageData?.game ?? null,
          currentStage: stageData?.currentStage ?? null,
          nextDay: stageData?.nextDay ?? null,
        });
        puzzleId = puzzleId ?? stageData?.game?.puzzle_id ?? null;

        const minutes = Number(themeData?.waiting_timer_minutes);
        const seconds = Number(themeData?.waiting_timer_seconds);
        const validMinutes = Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
        const validSeconds = Number.isFinite(seconds) && seconds >= 0 && seconds <= 59 ? seconds : null;
        const totalConfigured = (validMinutes ?? 0) * 60 + (validSeconds ?? 0);
        const durationSeconds =
          validMinutes == null && validSeconds == null
            ? DEFAULT_WAITING_SECONDS
            : totalConfigured > 0
              ? Math.round(totalConfigured)
              : DEFAULT_WAITING_SECONDS;

        // Seed the local reference before subscribing so the ring doesn't flash the
        // hardcoded default while waiting for the first Firebase snapshot. The
        // countdown itself is never auto-started here — startedAt is only ever set
        // by an explicit admin Start/Restart/Skip action (see rules.jsx), so players
        // just see a static "waiting for host to start" state until then.
        waitingRoomStateRef.current = { startedAt: null, durationSeconds };
        setRingDuration(durationSeconds);
        setRemainingSeconds(durationSeconds);

        const unsubUsers = subscribeSessionUsers(fbSessionId, (data) => {
          usersMapRef.current = data || {};
          setUsersMap(data || {});
        });
        const unsubWaitingRoom = subscribeWaitingRoom(fbSessionId, (data) => {
          if (!data || !data.startedAt) {
            setTimerStarted(false);
            return;
          }
          setTimerStarted(true);
          const nextStartedAt = Number(data.startedAt);
          // Not `Number(data.durationSeconds) || durationSeconds` — that falls back to the
          // original duration when durationSeconds is legitimately 0 (Skip sets exactly
          // that, to end the countdown immediately), since 0 is falsy in JS. That bug is
          // why Skip appeared to do nothing and the lobby kept counting down as before.
          const rawDuration = Number(data.durationSeconds);
          const nextDurationSeconds = Number.isFinite(rawDuration) ? rawDuration : durationSeconds;
          waitingRoomStateRef.current = { startedAt: nextStartedAt, durationSeconds: nextDurationSeconds };
          setRingDuration(nextDurationSeconds);
          setEstimatedStartLabel(
            new Date(nextStartedAt + nextDurationSeconds * 1000).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })
          );
          setRemainingSeconds(computeRemaining());
        });

        unsubscribersRef.current.push(unsubUsers, unsubWaitingRoom);
        setLoading(false);
      } catch (err) {
        if (isSessionEndedError(err)) {
          // R2-P08: the activity already ended while this player was still
          // arriving — no tile is allocated; show them the finished canvas.
          initCalledRef.current = true;
          navigate('/canvas-reveal', { replace: true });
          return;
        }
        setMessage(err?.message || 'Failed to load waiting room.');
        setLoading(false);
      }
    };

    run();
  }, [themeStatus, themeData, authUser, adminToken, currentTheme, computeRemaining, navigate]);

  // Clean up Firebase listeners on unmount.
  useEffect(() => {
    return () => {
      unsubscribersRef.current.forEach((unsub) => {
        try {
          unsub?.();
        } catch {
          /* ignore */
        }
      });
      unsubscribersRef.current = [];
    };
  }, []);

  // NOTE: players used to call endCanvasActivity({ presentUserIds }) here to auto-fill
  // tiles of people who never arrived. The server only allows that partial form for the
  // HOST (it cannot verify presence), so every player's call was refused - ~2,000
  // pointless requests in the exact second everyone enters the canvas. Auto-filling
  // absent players is now left to the host / the end-of-activity finalize.

  // Drift-resistant 1s tick: always recompute from the shared startedAt/durationSeconds
  // reference (same pattern as puzzle.jsx's getRemainingSeconds) rather than decrementing,
  // so an admin Reset (new startedAt via the subscription) is reflected immediately too.
  useEffect(() => {
    if (loading) return undefined;

    const tick = () => {
      const remaining = computeRemaining();
      setRemainingSeconds(remaining);
      if (remaining <= 0 && !navigatedRef.current) {
        navigatedRef.current = true;
        if (tickIntervalRef.current) {
          window.clearInterval(tickIntervalRef.current);
          tickIntervalRef.current = null;
        }
        // Stagger the hand-off: ~2,000 browsers all mount the canvas (theme, tile image,
        // clock, Firebase writes) in the same second otherwise. 0-3 s is imperceptible.
        window.setTimeout(() => navigate('/canvas', { replace: true }), randomDelayMs(LOBBY_HANDOFF_SPREAD_MS));
      }
    };

    tick();
    tickIntervalRef.current = window.setInterval(tick, 1000);

    return () => {
      if (tickIntervalRef.current) {
        window.clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
    };
  }, [loading, computeRemaining, navigate]);

  const playersJoined = Object.keys(usersMap || {}).length;

  const avatarEntries = useMemo(() => {
    return Object.entries(usersMap || {}).map(([uid, data]) => ({
      id: uid,
      name: data?.name || 'Player',
    }));
  }, [usersMap]);

  const visibleAvatars = avatarEntries.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = Math.max(0, avatarEntries.length - visibleAvatars.length);

  const progress = ringDuration > 0 ? Math.max(0, Math.min(1, remainingSeconds / ringDuration)) : 0;
  const dashOffset = RING_CIRCUMFERENCE * (1 - progress);

  const pageStyle = {
    '--wr-text': visuals.textColor,
    '--wr-accent': visuals.buttonBgColor,
    '--wr-box-text': visuals.boxTextColor,
  };

  if (themeStatus === 'loading' || themeStatus === 'idle' || loading) {
    return (
      <div className="wr-page" style={pageStyle}>
        <div className="wr-loader-wrap" aria-busy="true" aria-label="Loading waiting room">
          <div className="wr-loader" />
        </div>
      </div>
    );
  }

  if (message) {
    return (
      <div className="wr-page" style={pageStyle}>
        <p className="wr-error">{message}</p>
      </div>
    );
  }

  return (
    <div className="wr-page" style={pageStyle}>
      <div className="wr-card">
        <h1 className="wr-heading">Waiting for everyone to join...</h1>

        <div className="wr-main-row">
          <div className="wr-ring-wrap">
            <svg className="wr-ring" viewBox="0 0 160 160" aria-hidden>
              <circle className="wr-ring-track" cx="80" cy="80" r={RING_RADIUS} />
              <circle
                className="wr-ring-progress"
                cx="80"
                cy="80"
                r={RING_RADIUS}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
              />
            </svg>
            <div className="wr-ring-content">
              {timerStarted ? (
                <>
                  <span className="wr-ring-label">Starts in</span>
                  <span className="wr-ring-time">{formatGameTimer(Math.ceil(remainingSeconds))}</span>
                  {estimatedStartLabel ? (
                    <span className="wr-ring-caption">
                      Estimated start Time
                      <br />
                      {estimatedStartLabel}
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="wr-ring-label">Waiting for host to start…</span>
              )}
            </div>
          </div>

          <div className="wr-players-col">
            <div className="wr-players-pill">
              Players Joined {playersJoined}
            </div>
            <div className="wr-avatar-row">
              {visibleAvatars.map((entry, index) => (
                <span
                  key={entry.id}
                  className="wr-avatar"
                  style={{ background: avatarColorForName(entry.name, index) }}
                  title={entry.name}
                >
                  {initialForName(entry.name)}
                </span>
              ))}
              {overflowCount > 0 ? (
                <span className="wr-avatar wr-avatar-overflow">+{overflowCount}</span>
              ) : null}
            </div>
          </div>
        </div>

        <p className="wr-message-primary">Get ready to create something beautiful together!</p>
        <p className="wr-message-secondary">
          Once all players join, the activity will begin automatically
        </p>
      </div>
    </div>
  );
}
